import type {
  CustomerCreateRequest,
  CustomerResponse,
  CustomerUpdateRequest,
  CustomerListParams,
  CustomerListResponse,
  CustomerListWithCountParams,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Flatten list params for the query string.
 *
 * `profile_ids` must go out as ONE comma-separated value. The client
 * serializes arrays as repeated keys (`?profile_ids=a&profile_ids=b`), and the
 * customers endpoints cannot parse that — the whole request fails, rather than
 * the second id being ignored. So the join happens here, once, instead of at
 * four call sites.
 */
function toQuery(
  params?: CustomerListParams,
): Record<string, string | number | boolean | undefined> | undefined {
  if (!params) return undefined;
  const { profile_ids, ...rest } = params;
  const query = rest as Record<string, string | number | boolean | undefined>;
  // An empty array means "no shop filter", which is the absent key — sending
  // `profile_ids=` would work too, but omitting it keeps the URL honest.
  if (profile_ids && profile_ids.length > 0) {
    query.profile_ids = profile_ids.join(',');
  }
  return query;
}

/** Create and manage customer profiles. */
export class Customers {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create a new customer.
   *
   * @param params - Customer creation parameters (name, email, phone, address, etc.).
   * @returns The created customer.
   *
   * @example
   * ```typescript
   * const customer = await delopay.customers.create({
   *   email: 'alice@example.com',
   *   name: 'Alice Smith',
   * });
   * ```
   */
  async create(params: CustomerCreateRequest): Promise<CustomerResponse> {
    return this.request('POST', '/customers', { body: params });
  }

  /**
   * Retrieve a customer by their ID.
   *
   * @param customerId - The unique customer ID.
   * @returns The customer.
   *
   * @example
   * ```typescript
   * const customer = await delopay.customers.retrieve('cus_abc123');
   * ```
   */
  async retrieve(customerId: string): Promise<CustomerResponse> {
    return this.request('GET', `/customers/${encodeURIComponent(customerId)}`);
  }

  /**
   * Update an existing customer's details.
   *
   * @param customerId - The customer ID to update.
   * @param params - Fields to update (name, email, address, metadata, etc.).
   * @returns The updated customer.
   */
  async update(customerId: string, params: CustomerUpdateRequest): Promise<CustomerResponse> {
    return this.request('POST', `/customers/${encodeURIComponent(customerId)}`, { body: params });
  }

  /**
   * Delete a customer and all their saved payment methods.
   *
   * @param customerId - The customer ID to delete.
   * @returns The deleted customer object.
   */
  async delete(customerId: string): Promise<CustomerResponse> {
    return this.request('DELETE', `/customers/${encodeURIComponent(customerId)}`);
  }

  /**
   * List customers, optionally filtered by customer id/name/email prefix,
   * shop (`profile_id`), or project (`project_id`).
   *
   * @param params - Optional filter and pagination parameters.
   * @returns Array of customer objects.
   *
   * @example
   * ```typescript
   * // Customers who have transacted in a specific shop.
   * const customers = await delopay.customers.list({ profile_id: 'pro_abc123' });
   *
   * // Several shops at once (unions with profile_id / project_id).
   * const many = await delopay.customers.list({
   *   profile_ids: ['pro_abc123', 'pro_def456'],
   * });
   * ```
   */
  async list(params?: CustomerListParams): Promise<CustomerResponse[]> {
    return this.request('GET', '/customers/list', {
      query: toQuery(params),
    });
  }

  // --- OLAP extensions (Task 4.6) ---

  /**
   * List customers with count. Supports the same `profile_id` / `project_id`
   * shop filters as {@link list}. `GET /customers/list-with-count`
   */
  async listWithCount(params?: CustomerListWithCountParams): Promise<CustomerListResponse> {
    return this.request('GET', '/customers/list-with-count', {
      query: toQuery(params),
    });
  }

  /**
   * List customers scoped to the authenticated dashboard user's shop
   * (business profile). The JWT auto-scopes to its own `profile`; an explicit
   * `profile_id` / `project_id` outside that scope is rejected with
   * `AccessForbidden`. `GET /customers/profile/list`
   *
   * @param params - Optional filter and pagination parameters.
   * @returns Array of customer objects.
   */
  async listByProfile(params?: CustomerListParams): Promise<CustomerResponse[]> {
    return this.request('GET', '/customers/profile/list', {
      query: toQuery(params),
    });
  }

  /**
   * Profile-scoped variant of {@link listWithCount}.
   * `GET /customers/profile/list-with-count`
   */
  async listByProfileWithCount(
    params?: CustomerListWithCountParams,
  ): Promise<CustomerListResponse> {
    return this.request('GET', '/customers/profile/list-with-count', {
      query: toQuery(params),
    });
  }

  /**
   * Retrieve one customer, scoped to the authenticated dashboard user's shop
   * (business profile). The shop-scoped counterpart of {@link retrieve}, and
   * what makes a shop's own customer list clickable: the list is
   * `ProfileCustomerRead` while {@link retrieve} is merchant-level, so a shop
   * member could see a customer they could not open.
   *
   * A customer belonging to a sibling shop answers `CustomerNotFound` — the
   * same answer as a customer that does not exist, deliberately, so one shop
   * cannot enumerate another's customer ids.
   *
   * `GET /customers/profile/{customerId}`
   *
   * @param customerId - The unique customer ID.
   * @returns The customer, with `profile_ids` narrowed to the caller's own shop.
   */
  async retrieveByProfile(customerId: string): Promise<CustomerResponse> {
    return this.request('GET', `/customers/profile/${encodeURIComponent(customerId)}`);
  }

  /**
   * Create a customer from a shop, recording that it belongs to that shop so
   * the shop can see the customer it just created. The shop is taken from the
   * credential; a caller-chosen `customer_id` is rejected.
   *
   * `POST /customers/profile`
   *
   * @param params - The customer to create.
   * @returns The created customer.
   */
  async createByProfile(params: CustomerCreateRequest): Promise<CustomerResponse> {
    return this.request('POST', '/customers/profile', { body: params });
  }

  /**
   * Update a customer from a shop, if the customer belongs to that shop.
   * A sibling shop's customer answers `CustomerNotFound`, as with
   * {@link retrieveByProfile}.
   *
   * `POST /customers/profile/{customerId}`
   *
   * @param customerId - The customer ID to update.
   * @param params - Fields to update.
   * @returns The updated customer.
   */
  async updateByProfile(
    customerId: string,
    params: CustomerUpdateRequest,
  ): Promise<CustomerResponse> {
    return this.request('POST', `/customers/profile/${encodeURIComponent(customerId)}`, {
      body: params,
    });
  }

  /** List mandates for a customer. `GET /customers/{customerId}/mandates` */
  async listMandates(customerId: string): Promise<Record<string, unknown>[]> {
    return this.request('GET', `/customers/${encodeURIComponent(customerId)}/mandates`);
  }

  /**
   * List one customer's mandates, scoped to the caller's shop. Same membership
   * rule as {@link retrieveByProfile}: a sibling shop's customer answers
   * `CustomerNotFound` before any mandate is read.
   *
   * `GET /customers/profile/{customerId}/mandates`
   */
  async listMandatesByProfile(customerId: string): Promise<Record<string, unknown>[]> {
    return this.request('GET', `/customers/profile/${encodeURIComponent(customerId)}/mandates`);
  }
}
